import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, brl, catName, errMsg, CATS } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const toLocal = (iso) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const EMPTY = { title: "", description: "", category: "todas", is_main: false, entry_fee: 0, prize: "", deadline: toLocal(new Date(Date.now() + 14 * 86400000).toISOString()), status: "active", cover_url: "" };

const ContestForm = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const save = async () => {
    const body = { ...f, entry_fee: Number(f.entry_fee) || 0, deadline: new Date(f.deadline).toISOString(), cover_url: f.cover_url || null };
    try {
      if (f.id) await api.put(`/admin/contests/${f.id}`, body);
      else await api.post("/admin/contests", body);
      toast.success("Concurso salvo");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto border-white/10 bg-[#0E0E15]" data-testid="admin-contest-form">
        <DialogHeader><DialogTitle className="font-display text-3xl font-black uppercase">{f.id ? "Editar concurso" : "Novo concurso"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Título</Label><Input data-testid="contest-form-title" value={f.title} onChange={set("title")} /></div>
          <div className="space-y-1.5"><Label>Descrição</Label><Textarea data-testid="contest-form-description" value={f.description} onChange={set("description")} /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Modalidade</Label>
              <Select value={f.category} onValueChange={set("category")}>
                <SelectTrigger data-testid="contest-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as modalidades</SelectItem>
                  {CATS.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={f.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="contest-form-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Aberto</SelectItem><SelectItem value="closed">Encerrado</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Taxa de inscrição (R$)</Label><Input data-testid="contest-form-fee" type="number" step="0.01" min="0" value={f.entry_fee} onChange={set("entry_fee")} /></div>
            <div className="space-y-1.5"><Label>Prazo final</Label><Input data-testid="contest-form-deadline" type="datetime-local" value={f.deadline} onChange={set("deadline")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Prêmio</Label><Input data-testid="contest-form-prize" value={f.prize} onChange={set("prize")} placeholder="Ex: R$ 5.000 + Câmera" /></div>
          <div className="space-y-1.5"><Label>URL da imagem de capa (opcional)</Label><Input data-testid="contest-form-cover" value={f.cover_url || ""} onChange={set("cover_url")} /></div>
          <div className="flex items-center justify-between rounded-xl border border-white/10 p-4">
            <div><p className="font-semibold">Concurso principal</p><p className="text-xs text-slate-400">Destaque na home; foto grátis não participa</p></div>
            <Switch data-testid="contest-form-is-main" checked={f.is_main} onCheckedChange={set("is_main")} />
          </div>
          <button data-testid="contest-form-save" onClick={save} className="btn-neon w-full rounded-full py-3 font-semibold">Salvar concurso</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AdminContests = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/contests").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);

  const del = async (c) => {
    if (!window.confirm(`Excluir "${c.title}"?`)) return;
    await api.delete(`/admin/contests/${c.id}`);
    toast.success("Concurso excluído");
    load();
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-3xl font-black uppercase">Concursos</h2>
        <button data-testid="admin-new-contest-btn" onClick={() => setEditing(EMPTY)} className="btn-neon flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"><Plus className="h-4 w-4" /> Novo concurso</button>
      </div>
      <div className="glass overflow-x-auto rounded-xl">
        <table className="w-full text-sm" data-testid="admin-contests-table">
          <thead className="font-mono-tech text-[11px] uppercase tracking-widest text-slate-500">
            <tr className="border-b border-white/10 text-left"><th className="p-4">Concurso</th><th className="p-4">Modalidade</th><th className="p-4">Taxa</th><th className="p-4">Fotos</th><th className="p-4">Status</th><th className="p-4" /></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-white/5 last:border-0" data-testid={`admin-contest-row-${c.id}`}>
                <td className="p-4 font-semibold">{c.is_main && <Zap className="mr-1 inline h-3.5 w-3.5 text-lime-300" />}{c.title}</td>
                <td className="p-4 text-slate-400">{catName(c.category)}</td>
                <td className="p-4">{c.entry_fee > 0 ? brl(c.entry_fee) : "Grátis"}</td>
                <td className="p-4">{c.entries_count}</td>
                <td className="p-4"><span className={c.status === "active" ? "text-lime-300" : "text-red-300"}>{c.status === "active" ? "Aberto" : "Encerrado"}</span></td>
                <td className="p-4">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`admin-edit-contest-${c.id}`} onClick={() => setEditing({ ...c, deadline: toLocal(c.deadline) })} className="rounded-full border border-white/10 p-2 hover:border-cyan-400"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`admin-delete-contest-${c.id}`} onClick={() => del(c)} className="rounded-full border border-white/10 p-2 hover:border-red-400 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ContestForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};
